import { useNavigate } from 'react-router-dom'
import { useWizardStore } from '@/store/wizardStore'
import type { Lang } from '@/hooks/useLang'

interface Step10ResultProps { lang: Lang }

const BAND_RANGES: Record<string, string> = {
  companion: '12 000 – 24 000 €',
  assistant_pro: '24 000 – 48 000 €',
  operator_specialist: '48 000 – 96 000 €',
}

const BAND_LABELS: Record<string, { fr: string; en: string }> = {
  companion: { fr: 'Companion', en: 'Companion' },
  assistant_pro: { fr: 'Assistant Pro', en: 'Assistant Pro' },
  operator_specialist: { fr: 'Operator Specialist', en: 'Operator Specialist' },
}

const AI_LEVEL_LABEL: Record<number, { fr: string; en: string }> = {
  1: { fr: 'Téléopéré', en: 'Teleoperated' },
  2: { fr: 'Assisté', en: 'Assisted' },
  3: { fr: 'Supervisé', en: 'Supervised' },
  4: { fr: 'Autonome', en: 'Autonomous' },
  5: { fr: 'Pleinement autonome', en: 'Fully autonomous' },
}

const COPY = {
  fr: {
    tagline: 'Résumé de configuration',
    title: 'Votre robot est prêt.',
    subtitle: 'Revue de votre configuration robot.fric.link. Générez votre cahier des charges ou modifiez les paramètres.',
    feasibility: 'Faisabilité estimée',
    complexity: 'Indice de complexité',
    pricing: 'Tranche budgétaire',
    savePdf: 'Générer le cahier des charges',
    saveDesign: 'Sauvegarder la conception',
    restart: 'Nouvelle configuration',
    role: 'Rôle',
    env: 'Environnement',
    missions: 'Missions',
    aiLevel: 'Niveau IA',
    height: 'Hauteur',
    payload: 'Charge utile',
    hours: 'Autonomie',
    budget: 'Budget',
  },
  en: {
    tagline: 'Configuration summary',
    title: 'Your robot is ready.',
    subtitle: 'Review of your robot.fric.link configuration. Generate your specs sheet or edit parameters.',
    feasibility: 'Estimated feasibility',
    complexity: 'Complexity index',
    pricing: 'Budget tier',
    savePdf: 'Generate specifications sheet',
    saveDesign: 'Save design',
    restart: 'New configuration',
    role: 'Role',
    env: 'Environment',
    missions: 'Missions',
    aiLevel: 'AI level',
    height: 'Height',
    payload: 'Payload',
    hours: 'Autonomy',
    budget: 'Budget',
  },
}

function feasibilityScore(aiLevel: number, missions: number, hours: number): number {
  const base = 85
  const aiPenalty = Math.max(0, (aiLevel - 3) * 5)
  const missionPenalty = Math.max(0, (missions - 3) * 3)
  const hoursPenalty = Math.max(0, (hours - 16) * 1)
  return Math.min(98, Math.max(40, base - aiPenalty - missionPenalty - hoursPenalty))
}

export default function Step10Result({ lang }: Step10ResultProps) {
  const navigate = useNavigate()
  const { draft, reset } = useWizardStore()
  const t = COPY[lang]

  const score = feasibilityScore(
    draft.step4_ai_level ?? 3,
    draft.step3_missions?.length ?? 1,
    draft.step7_hours_per_day ?? 8
  )
  const complexity = Math.round(
    ((draft.step4_ai_level ?? 1) / 5) * 40 +
    ((draft.step3_missions?.length ?? 1) / 8) * 30 +
    ((draft.step7_hours_per_day ?? 8) / 24) * 30
  )

  const budgetKey = draft.step8_budget ?? 'companion'
  const bandLabel = BAND_LABELS[budgetKey]?.[lang] ?? budgetKey
  const bandRange = BAND_RANGES[budgetKey] ?? '—'

  const summaryRows = [
    { label: t.role, value: draft.step1_role?.replace(/_/g, ' ') ?? '—' },
    { label: t.env, value: draft.step2_environment?.replace(/_/g, ' ') ?? '—' },
    { label: t.missions, value: `${draft.step3_missions?.length ?? 0}` },
    { label: t.aiLevel, value: AI_LEVEL_LABEL[draft.step4_ai_level ?? 1]?.[lang] ?? '—' },
    { label: t.height, value: draft.step5_height_cm ? `${draft.step5_height_cm} cm` : '—' },
    { label: t.payload, value: draft.step5_payload_kg ? `${draft.step5_payload_kg} kg` : '—' },
    { label: t.hours, value: draft.step7_hours_per_day ? `${draft.step7_hours_per_day}h/j` : '—' },
    { label: t.budget, value: bandLabel },
  ]

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Left — score cards */}
      <div className="lg:col-span-1 flex flex-col gap-6">
        {/* Feasibility */}
        <div className="glass-card p-8 rounded-xl border border-outline-variant/20">
          <span className="label-technical block mb-4">{t.feasibility}</span>
          <div className="font-headline text-6xl font-bold text-primary-container">{score}<span className="text-2xl text-on-surface-variant">%</span></div>
          <div className="mt-4 h-1.5 w-full bg-surface-container-highest rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary-container to-secondary rounded-full transition-all duration-1000" style={{ width: `${score}%` }} />
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-primary-container font-label">
            <span className="material-symbols-outlined text-sm">check_circle</span>
            {score >= 80 ? (lang === 'fr' ? 'Configuration réalisable' : 'Feasible configuration') : (lang === 'fr' ? 'Configuration complexe' : 'Complex configuration')}
          </div>
        </div>

        {/* Complexity */}
        <div className="glass-card p-8 rounded-xl border border-outline-variant/20">
          <span className="label-technical block mb-4">{t.complexity}</span>
          <div className="font-headline text-6xl font-bold text-secondary">{complexity}<span className="text-2xl text-on-surface-variant">/100</span></div>
          <div className="mt-4 h-1.5 w-full bg-surface-container-highest rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-secondary/40 to-secondary rounded-full transition-all duration-1000" style={{ width: `${complexity}%` }} />
          </div>
        </div>

        {/* Budget */}
        <div className="glass-card p-8 rounded-xl border border-outline-variant/20">
          <span className="label-technical block mb-3">{t.pricing}</span>
          <div className="font-headline text-xl font-bold text-primary mb-1">{bandLabel}</div>
          <div className="text-on-surface-variant text-sm font-body">{bandRange}</div>
        </div>
      </div>

      {/* Right — summary + actions */}
      <div className="lg:col-span-2 flex flex-col gap-6">
        <div className="glass-card p-8 rounded-xl border border-outline-variant/20">
          <h2 className="font-headline text-3xl font-bold text-on-surface mb-2">{t.title}</h2>
          <p className="text-on-surface-variant text-sm font-body leading-relaxed mb-8">{t.subtitle}</p>

          <div className="grid grid-cols-2 gap-3">
            {summaryRows.map((row) => (
              <div key={row.label} className="bg-surface-container rounded-lg px-4 py-3">
                <div className="label-technical mb-1">{row.label}</div>
                <div className="font-headline text-sm font-bold text-on-surface capitalize">{row.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA buttons */}
        <div className="flex flex-wrap gap-4">
          <button className="btn-primary text-sm flex-1 min-w-[200px] justify-center">
            <span className="material-symbols-outlined text-lg">picture_as_pdf</span>
            {t.savePdf}
          </button>
          <button className="btn-secondary text-sm flex-1 min-w-[160px] justify-center">
            <span className="material-symbols-outlined text-lg">save</span>
            {t.saveDesign}
          </button>
          <button
            onClick={() => { reset(); void navigate('/configurator') }}
            className="text-on-surface-variant hover:text-on-surface font-headline text-sm tracking-widest uppercase transition-colors px-6 py-3"
          >
            {t.restart}
          </button>
        </div>
      </div>
    </div>
  )
}
